import apiClient from "../utils/axiosConfig";
import { getApiUrl, API_ENDPOINTS } from "../Configurations/config";

const SALES_TIMEOUT_MS = 30000;

const cache = new Map();

const getCacheKey = (key, params) => {
  return `${key}_${JSON.stringify(params)}`;
};

export async function getFiscalYears() {
  try {
    const response = await apiClient.get(getApiUrl(API_ENDPOINTS.SALES.FISCAL_YEARS), {
      timeout: SALES_TIMEOUT_MS,
    });
    return response.data;
  } catch {
    return [];
  }
}

export async function getMonths(fiscalYear) {
  try {
    const response = await apiClient.get(getApiUrl(API_ENDPOINTS.SALES.MONTHS), {
      params: {
        fiscal_year: fiscalYear,
      },
      timeout: SALES_TIMEOUT_MS,
    });

    return response.data;
  } catch {
    return [];
  }
}

export async function getDivisions(month) {
  try {
    const monthValue =
      typeof month === "object"
        ? month.id || month.value
        : month;

    const formattedMonth = (monthValue || "").replace("-", "");

    if (!formattedMonth) {
      return [];
    }

    const response = await apiClient.get(getApiUrl(API_ENDPOINTS.SALES.DIVISIONS), {
      params: { month: formattedMonth },
      timeout: SALES_TIMEOUT_MS,
    });

    return response.data;
  } catch {
    return [];
  }
}

export async function getStates(month, divisionId) {
  try {
    const getValue = (val) => {
      if (!val) return "";

      if (typeof val === "object") {
        return val.id || val.value || val.label || val.name || "";
      }

      return val;
    };

    const monthValue = getValue(month);
    const divisionValue = getValue(divisionId);

    const formattedMonth = monthValue.replace("-", "");

    const divisionName = divisionValue
      .replace("division-", "")
      .trim()
      .toUpperCase();

    if (!formattedMonth || !divisionName) {
      return [];
    }

    const response = await apiClient.get(getApiUrl(API_ENDPOINTS.SALES.STATE_SUMMARY), {
      params: {
        month: formattedMonth,
        division: divisionName,
      },
      timeout: SALES_TIMEOUT_MS,
    });

    const data = response?.data || [];

    return data.map((item, index) => ({
      id: item.id || `state-${item.value}-${index}`,

      label: item.label || item.value,
      value: item.value,
      name: item.label || item.value,

      salesLakh: item.total_sales
        ? item.total_sales / 100000
        : 0,

      employees: item.employees ?? 0,
      achievement: item.achievement ?? 0,
    }));
  } catch {
    return [];
  }
}

export async function getTerritories(month, divisionId, stateId, options = {}) {
  const { signal } = options;
  try {
    const getValue = (val) => {
      if (!val) return "";
      if (typeof val === "object") {
        return val.id || val.value || val.label || val.name || "";
      }
      return val;
    };

    const monthValue = getValue(month);
    const divisionValue = getValue(divisionId);
    const stateValue = getValue(stateId);

    const formattedMonth = monthValue.replace("-", "");

    const divisionName = divisionValue
      .replace("division-", "")
      .trim()
      .toUpperCase();

    const stateName = stateValue
      .replace("state-", "")
      .split("-")[0]
      .trim()
      .toUpperCase();

    if (!formattedMonth || !divisionName || !stateName) {
      return [];
    }

    const params = {
      month: formattedMonth,
      division: divisionName,
      state: stateName,
    };

    const cacheKey = getCacheKey("territories", params);

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }

    const response = await apiClient.get(getApiUrl(API_ENDPOINTS.SALES.TERRITORIES), {
      params,
      signal,
      timeout: SALES_TIMEOUT_MS,
    });
    const data = Array.isArray(response?.data) ? response.data : [];

    const formattedData = data.map((item, index) => {
      const name = (item.label || item.name || item.value || "")
        .toUpperCase()
        .trim();

      return {
        id: item.id || `territory-${index}`,
        label: name,
        value: name,
        name: name,
        salesLakh: item.salesLakh ?? 0,
        employees: item.employees ?? 0,
        achievement: item.achievement ?? 0,
        target: item.target ?? 0,
      };
    });

    cache.set(cacheKey, formattedData);

    const territoryMap = new Map();
    formattedData.forEach((t) => {
      const key = (t.value || "").toUpperCase().trim();
      if (key) territoryMap.set(key, t);
    });

    cache.set(`${cacheKey}_map`, territoryMap);

    return formattedData;
  } catch {
    return [];
  }
}

export async function getSummary(params, options = {}) {
  const { signal } = options;
  try {
    const getValue = (val) => {
      if (!val) return "";

      if (typeof val === "object") {
        return val.label || val.value || val.name || val.id || "";
      }

      return val;
    };

    const formatMonth = (month) => {
      if (!month) return "";

      if (month.includes(" ")) return month;

      const [year, m] = month.split("-");
      const date = new Date(year, m - 1);

      return date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    };

    const monthValue =
      typeof params.month === "object"
        ? params.month.id
        : params.month || "";

    const formattedMonth = monthValue.replace("-", "");

    const division = getValue(params.divisionId)
      .replace("division-", "")
      .toUpperCase()
      .trim();

    const state = getValue(params.stateId)
      .replace("state-", "")
      .split("-")[0]
      .toUpperCase()
      .trim();

    const territoryValue = getValue(params.territoryId)
      .toUpperCase()
      .trim();

    const cacheKey = getCacheKey("territories", {
      month: formattedMonth,
      division,
      state,
    });

    let territoryMap = cache.get(`${cacheKey}_map`);

    if (!territoryMap) {
      await getTerritories(params.month, params.divisionId, params.stateId, { signal });
      territoryMap = cache.get(`${cacheKey}_map`);
    }

    const selected = territoryMap?.get(territoryValue);

    if (!selected) {
      return null;
    }

    const path = [
      formatMonth(monthValue),
      getValue(params.divisionId),
      getValue(params.stateId),
      getValue(params.territoryId),
    ].filter((v) => v && v !== "");

    const safeTarget = Array.isArray(selected.target)
      ? selected.target[0]
      : selected.target ?? 0;

    return {
      totalSales: selected.salesLakh ?? 0,
      totalEmployees: selected.employees ?? 0,
      achievement: selected.achievement ?? 0,
      path,
      target: safeTarget,
    };
  } catch {
    return null;
  }
}

export async function getEmployees(
  month,
  divisionId,
  stateId,
  territoryId,
  page = 1,
  limit = 5,
  options = {}
) {
  const { signal } = options;
  try {
    const getValue = (val) => {
      if (!val) return "";
      if (typeof val === "object") {
        return val.id || val.value || val.label || val.name || "";
      }
      return val;
    };

    const monthValue = getValue(month);
    const divisionValue = getValue(divisionId);
    const stateValue = getValue(stateId);
    const territoryValue = getValue(territoryId);

    const formattedMonth = monthValue.replace("-", "");

    const divisionName = divisionValue.toUpperCase().trim();
    const stateName = stateValue.trim();
    const territoryName = territoryValue.trim();

    if (!formattedMonth || !divisionName || !stateName || !territoryName) {
      return {
        data: [],
        page: 1,
        total_pages: 1,
        total: 0,
      };
    }

    const response = await apiClient.get(getApiUrl(API_ENDPOINTS.SALES.EMPLOYEES_DATA), {
      params: {
        month: formattedMonth,
        division: divisionName,
        state: stateName,
        territory: territoryName,
        page,
        limit,
      },
      signal,
      timeout: SALES_TIMEOUT_MS,
    });

    const res = response?.data || {};

    return {
      data: res.data || [],
      page: res.page || page,
      total_pages: res.total_pages || 1,
      total: res.total || 0,
    };
  } catch {
    return {
      data: [],
      page: 1,
      total_pages: 1,
      total: 0,
    };
  }
}

export async function getEmployeeDetails(employeeId, params) {
  try {
    const getValue = (val) => {
      if (!val) return "";
      if (typeof val === "object") {
        return val.id || val.value || val.label || val.name || "";
      }
      return val;
    };

    const monthValue = getValue(params.month);
    const divisionValue = getValue(params.division);
    const stateValue = getValue(params.state);
    const territoryValue = getValue(params.territory);

    const formattedMonth = monthValue.replace("-", "");

    const division = divisionValue
      .replace("division-", "")
      .toUpperCase()
      .trim();

    const state = stateValue
      .replace("state-", "")
      .split("-")[0]
      .toUpperCase()
      .trim();

    const territory = territoryValue
      .toUpperCase()
      .trim();

    if (!formattedMonth || !division || !state || !territory) {
      return null;
    }

    const response = await apiClient.get(
      getApiUrl(API_ENDPOINTS.SALES.EMPLOYEE_DETAILS(employeeId)),
      {
        params: {
          month: formattedMonth,
          division,
          state,
          territory,
        },
        timeout: SALES_TIMEOUT_MS,
      }
    );

    return response.data;
  } catch {
    return null;
  }
}

function buildEmployeeDetailQueryParams(params, employeeId) {
  const getValue = (val) => {
    if (!val) return "";
    if (typeof val === "object") {
      return val.id || val.value || val.label || val.name || "";
    }
    return val;
  };

  const monthValue = getValue(params.month);
  const divisionValue = getValue(params.division);
  const stateValue = getValue(params.state);
  const territoryValue = getValue(params.territory);

  const formattedMonth = monthValue.replace("-", "");

  const division = divisionValue
    .replace("division-", "")
    .toUpperCase()
    .trim();

  const state = stateValue
    .replace("state-", "")
    .split("-")[0]
    .toUpperCase()
    .trim();

  const territory = territoryValue.toUpperCase().trim();

  if (!formattedMonth || !division || !state || !territory) return null;

  return {
    month: formattedMonth,
    division,
    state,
    territory,
    employeeId,
  };
}

async function getEmployeeSection(employeeId, path, params, options = {}) {
  const qp = buildEmployeeDetailQueryParams(params, employeeId);
  if (!qp) {
    return null;
  }

  const { signal, page, limit } = options || {};

  const mergedParams = {
    ...qp,
    ...(page !== undefined ? { page } : {}),
    ...(limit !== undefined ? { limit } : {}),
  };

  const response = await apiClient.get(
    getApiUrl(API_ENDPOINTS.SALES.EMPLOYEE_SECTION(employeeId, path)),
    {
      params: mergedParams,
      signal,
      timeout: SALES_TIMEOUT_MS,
    }
  );

  return response.data;
}

export async function getEmployeeMain(employeeId, params, options = {}) {
  try {
    return await getEmployeeSection(employeeId, "main", params, options);
  } catch {
    return null;
  }
}

export async function getEmployeeBrandPerformance(employeeId, params, options = {}) {
  try {
    const res = await getEmployeeSection(
      employeeId,
      "brand-performance",
      params,
      options
    );

    if (res && typeof res === "object" && Array.isArray(res.data)) {
      return res;
    }

    if (Array.isArray(res)) {
      return {
        data: res,
        page: options?.page ?? 1,
        total_pages: 1,
        total: res.length,
      };
    }

    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  } catch {
    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  }
}

export async function getEmployeeMissedDoctors(employeeId, params, options = {}) {
  try {
    const res = await getEmployeeSection(
      employeeId,
      "missed-doctors",
      params,
      options
    );

    if (res && typeof res === "object" && Array.isArray(res.data)) {
      return {
        ...res,
        data: res.data.map((row = {}) => ({
          name: row.name ?? row.doctorName ?? row.full_name ?? "",
          specialty:
            row.specialty ?? row.speciality ?? row.specialityname ?? "",
          qualification: row.qualification ?? row.qualificationname ?? "",
          city: row.city ?? "",
        })),
      };
    }

    if (Array.isArray(res)) {
      return {
        data: res,
        page: options?.page ?? 1,
        total_pages: 1,
        total: res.length,
      };
    }

    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  } catch {
    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  }
}

export async function getEmployeePatchCoverage(employeeId, params, options = {}) {
  try {
    const res = await getEmployeeSection(
      employeeId,
      "patch-coverage",
      params,
      options
    );

    if (res && typeof res === "object" && Array.isArray(res.data)) {
      return {
        ...res,
        data: res.data.map((row = {}) => ({
          patch: row.patch ?? row.patchName ?? "",
          doctors: Number(row.doctors ?? 0),
          vfTarget: Number(row.vfTarget ?? row.vf_target ?? 0),
          actualVisits: Number(row.actualVisits ?? row.actual_visits ?? 0),
          vfAdherence: Number(
            row.vfAdherence ?? row.vf_adherence_percent ?? 0
          ),
        })),
      };
    }

    if (Array.isArray(res)) {
      return {
        data: res,
        page: options?.page ?? 1,
        total_pages: 1,
        total: res.length,
      };
    }

    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  } catch {
    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  }
}

export async function getEmployeeSpecialtyCoverage(employeeId, params, options = {}) {
  try {
    const res = await getEmployeeSection(
      employeeId,
      "specialty-coverage",
      params,
      options
    );

    if (res && typeof res === "object" && Array.isArray(res.data)) {
      return {
        ...res,
        data: res.data.map((row = {}) => ({
          specialty: row.specialty ?? row.speciality ?? "",
          speciality: row.speciality ?? row.specialty ?? "",
          doctors: Number(row.doctors ?? 0),
          vfTarget: Number(row.vfTarget ?? row.vf_target ?? 0),
          actualVisits: Number(row.actualVisits ?? row.actual_visits ?? 0),
          vfAdherence: Number(
            row.vfAdherence ?? row.vf_adherence_percent ?? 0
          ),
        })),
      };
    }

    if (Array.isArray(res)) {
      return {
        data: res,
        page: options?.page ?? 1,
        total_pages: 1,
        total: res.length,
      };
    }

    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  } catch {
    return {
      data: [],
      page: options?.page ?? 1,
      total_pages: 1,
      total: 0,
    };
  }
}

export async function getEmployeeMonthlyCoverageTrend(employeeId, params, options = {}) {
  try {
    const data = await getEmployeeSection(
      employeeId,
      "monthly-coverage-trend",
      params,
      options
    );
    return data || null;
  } catch {
    return null;
  }
}

export async function getEmployeeWeeklyPerformance(employeeId, params, options = {}) {
  try {
    const data = await getEmployeeSection(
      employeeId,
      "weekly-performance",
      params,
      options
    );
    return data || null;
  } catch {
    return null;
  }
}
